import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { api } from '../api';

const STATUS_LABELS = {
  draft: 'Draft',
  go: 'Pending Launch',
  running: 'Running',
  stopped: 'Stopped',
  archived: 'Archived',
  failed: 'Failed',
};

const STATUS_COLORS = {
  draft: 'text-gray-500',
  go: 'text-blue-600',
  running: 'text-green-600',
  stopped: 'text-yellow-600',
  archived: 'text-gray-400',
  failed: 'text-red-600',
};


export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [currentTimes, setCurrentTimes] = useState({
    ist: '',
    utc: '',
  });
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchStats = () => {
      api.getDashboardStats().then(setStats).catch((err) => setError(err.message));
    };

    const updateClock = () => {
      const now = new Date();

      const formatClock = (timeZone) => {
        const parts = new Intl.DateTimeFormat('en-IN', {
          timeZone,
          weekday: 'short',
          day: 'numeric',
          month: 'short',
          year: 'numeric',
          hour: '2-digit',
          minute: '2-digit',
          second: '2-digit',
          hour12: false,
        }).formatToParts(now);

        const values = Object.fromEntries(
          parts.map((part) => [part.type, part.value])
        );

        return `${values.weekday}, ${values.day} ${values.month} ${values.year} ${values.hour}:${values.minute}:${values.second}`;
      };

      setCurrentTimes({
        ist: formatClock('Asia/Kolkata'),
        utc: formatClock('UTC'),
      });
    };

    updateClock();

    const clockInterval = setInterval(updateClock, 1000);

    fetchStats();
    const interval = setInterval(() => {
      fetchStats();
      }, 5 * 60 * 1000);

    return () => {
      clearInterval(interval);
      clearInterval(clockInterval);
    };
  }, []);

  if (error) {
    return <Layout><div className="bg-red-50 text-red-600 text-sm rounded-lg px-3 py-2">{error}</div></Layout>;
  }

  if (!stats) {
    return <Layout><p className="text-gray-500">Loading...</p></Layout>;
  }

  const countFor = (status) => stats.statusCounts.find((s) => s.status === status)?.count || 0;
  const runningDashboard = stats.sourceSplit.find((s) => s.source === 'dashboard')?.count || 0;
  const runningPlatform = stats.sourceSplit.find((s) => s.source === 'platform')?.count || 0;

  return (
    <Layout>
      <h1 className="text-2xl font-bold text-gray-800 mb-6">Dashboard</h1>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-xs text-gray-400 uppercase mb-1">Active Clients</p>
          <p className="text-2xl font-bold text-brand-purple">{stats.activeClients}</p>
        </div>
        {Object.entries(STATUS_LABELS).map(([key, label]) => (
          <div key={key} className="bg-white rounded-xl shadow p-4">
            <p className="text-xs text-gray-400 uppercase mb-1">{label}</p>
            <p className={`text-2xl font-bold ${STATUS_COLORS[key]}`}>{countFor(key)}</p>
            {key === 'running' && (runningDashboard + runningPlatform > 0) && (
              <p className="text-[10px] text-gray-400 mt-1">{runningDashboard} created here, {runningPlatform} discovered</p>
            )}
          </div>
        ))}

        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-xs text-gray-400 uppercase tracking-wide">
            IST Time
          </p>
          <p className="text-sm font-semibold text-gray-800 mt-1">
            {currentTimes.ist || 'Loading...'}
          </p>
        </div>

        <div className="bg-white rounded-xl shadow p-4">
          <p className="text-xs text-gray-400 uppercase tracking-wide">
            UTC Time
          </p>
          <p className="text-sm font-semibold text-gray-800 mt-1">
            {currentTimes.utc || 'Loading...'}
          </p>
        </div>
      </div>


      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
        <div>
          <h2 className="text-lg font-bold text-gray-800 mb-3">⚠️ Needs Attention</h2>
          {stats.needsAttention.length === 0 ? (
            <div className="bg-white rounded-xl shadow p-5 text-sm text-gray-400">Nothing needs attention right now.</div>
          ) : (
            <div className="bg-white rounded-xl shadow divide-y divide-gray-100">
              {stats.needsAttention.map((a) => (
                <Link key={a.id} to={`/ads/${a.id}`} className="block px-4 py-3 hover:bg-brand-hero">
                  <div className="flex items-center justify-between">
                    <span className="font-medium text-gray-800 text-sm">{a.headline}</span>
                    <span className={`text-xs px-2 py-0.5 rounded-full ${a.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-yellow-100 text-yellow-700'}`}>
                      {a.status === 'failed' ? 'Failed' : a.creative_status}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">{a.client_name} — {a.brand} — {a.platform}</p>
                  {a.failure_reason && (
                    <p className="text-xs text-red-500 mt-1 truncate">{a.failure_reason}</p>
                  )}
                </Link>
              ))}
            </div>
          )}
        </div>

        <div>
          <h2 className="text-lg font-bold text-gray-800 mb-3">💰 Running Spend</h2>
          {stats.runningSpend.length === 0 ? (
            <div className="bg-white rounded-xl shadow p-5 text-sm text-gray-400">No ads currently running.</div>
          ) : (
            <div className="bg-white rounded-xl shadow divide-y divide-gray-100">
              {stats.runningSpend.map((a) => {
                const pct = a.daily_budget ? Math.round(((a.last_spend || 0) / a.daily_budget) * 100) : null;
                return (
                  <Link key={a.id} to={`/ads/${a.id}`} className="block px-4 py-3 hover:bg-brand-hero">
                    <div className="flex items-center justify-between">
                      <span className="font-medium text-gray-800 text-sm">{a.headline}</span>
                      <span className="text-sm text-gray-600">₹{a.last_spend ?? 0} / ₹{a.daily_budget}</span>
                    </div>
                    <p className="text-xs text-gray-500 mt-1">{a.client_name} — {a.platform}</p>
                    {pct !== null && (
                      <div className="w-full bg-gray-100 rounded-full h-1.5 mt-2">
                        <div
                          className={`h-1.5 rounded-full ${pct >= 100 ? 'bg-red-500' : pct >= 80 ? 'bg-yellow-500' : 'bg-green-500'}`}
                          style={{ width: `${Math.min(pct, 100)}%` }}
                        />
                      </div>
                    )}
                  </Link>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <div>
        <h2 className="text-lg font-bold text-gray-800 mb-3">Clients Overview</h2>
        <div className="bg-white rounded-xl shadow overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-left text-gray-500">
              <tr>
                <th className="px-4 py-3">Client</th>
                <th className="px-4 py-3">Total Ads</th>
                <th className="px-4 py-3">Live — Meta</th>
                <th className="px-4 py-3">Live — Google</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {stats.perClient.map((c) => (
                <tr key={c.id} className="border-t border-gray-100 hover:bg-brand-hero">
                  <td className="px-4 py-3 font-medium text-gray-800">
                    {c.client_name}
                    {c.has_missing_contact && (
                      <span className="ml-2 text-[10px] bg-yellow-100 text-yellow-700 px-1.5 py-0.5 rounded" title="Missing Telegram or email contact">⚠️ Contact info</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-gray-600">{c.total_ads}</td>
                  <td className="px-4 py-3 text-green-700 font-medium">{c.live_meta}</td>
                  <td className="px-4 py-3 text-green-700 font-medium">{c.live_google}</td>
                  <td className="px-4 py-3 text-right">
                    <Link to={`/ads/status?client=${c.id}`} className="text-brand-purple hover:underline text-xs">View Ads →</Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </Layout>
  );
}
